/** @format */

import {
	ATCEvent,
	Chain,
	CriteoViewItemMetadata,
	HomrGTGTContent,
	HomrProductStatsSlider,
	IHomrBanner,
	IHomrRichContent,
	IHomrState,
	IScoreCardSkuResponse,
	ISkuLocation,
	KillSwitches,
	LaunchConfig,
	ProcessedNonBundleProduct,
	ProductSku,
	PromotionDetailsMapV1,
	PromotionDetailsMapV2
} from '@shared/data-access/interfaces';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	effect,
	HostListener,
	inject,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Signal,
	ViewChild,
	viewChild,
	ViewContainerRef
} from '@angular/core';
import {
	AttributeOption,
	selectSelectedColorAttribute,
	selectShouldShowAlternativeInStockRecs
} from '@pdp/attributes/data-access';
import {
	BreakpointsService,
	ExclusiveContentService,
	HttpService,
	Logger,
	Store,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';
import {
	exclusiveApeValue,
	exclusiveBrands,
	exclusiveCTATypes,
	footwearApparelAttrNames,
	partyAttributes,
	partyAttrText
} from './in-stock-default.constants';
import {
	selectBrandLink,
	selectIsBOPISLOnly,
	selectIsComingSoon,
	selectIsProductWarrantyEligible,
	selectKillswitch,
	selectNearbyAvailableStoreCount,
	selectProductHasColorWithStylitics,
	selectResolvedSku,
	selectResolvedSkuPartNumber,
	selectStyliticsWidgetShown,
	selectTextileLabelInfo
} from '@pdp/data-access/state';
import {
	selectIsInStoreOnlyProduct,
	selectShouldShowOutOfStockRecs,
	setNearbyStoresButtonEnabledFlag
} from '@pdp/add-to-cart/data-access';

import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@shared/authentication/data-access';
import { BreakPointNew } from '@shared/data-access/enums';
import { CertonaService } from '@product-recommendations/data-access';
import { DynamicProductTemplateModules } from '@pdp/data-access/enums';
import { Store as NgRxStore } from '@ngrx/store';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductService } from '@shared/data-access/product';
import { QueryStringParamService } from '@pdp/data-access/services';
import { ReviewHighlyRatedFeaturesModalComponent } from '@product-reviews';
import { PLA_PLACEHOLDER_HIDDEN_CLASS } from '@shared/data-access/constants';
import { selectHomrGTGTContent, selectUserStoreName } from '@state/data-access';

@Component({
	selector: 'pdp-templates-in-stock-default',
	templateUrl: './in-stock-default.component.html',
	styleUrl: './in-stock-default.component.scss',
	standalone: false
})
export class InStockDefaultComponent implements OnInit, OnDestroy, AfterViewInit {
	private readonly document = inject(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly window = inject(WINDOW) as Window;
	private readonly analyticsService = inject(ProductAnalyticsService);
	private readonly authService = inject(AuthenticationService);
	private readonly certonaService = inject(CertonaService);
	private readonly exclusiveContentService = inject(ExclusiveContentService);
	private readonly httpService = inject(HttpService);
	private readonly logger = inject(Logger);
	private readonly ngRxstore = inject(NgRxStore);
	private readonly productService = inject(ProductService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly store = inject(Store);
	private readonly utilityService = inject(UtilityService);
	private readonly route = inject(ActivatedRoute);
	readonly chain = inject(Chain);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);

	@ViewChild('lazyHomrSketchfab3d', { read: ViewContainerRef, static: false })
	HomrSketchfab3d: ViewContainerRef;
	highlyRatedFeaturesModal: Signal<ReviewHighlyRatedFeaturesModalComponent> = viewChild.required(
		'highlyRatedFeaturesModal'
	);
	/**
	 * @description
	 * Reference to the view position where the above the fold content will be placed
	 */
	@ViewChild('targetAboveTheFoldViewRef', { read: ViewContainerRef })
	targetAboveTheFoldViewRef: ViewContainerRef;

	/**
	 * @description
	 * Reference to the initial view position of the section before it is moved into this
	 * component's template
	 */
	@Input() sourceAboveTheFoldViewRef: ViewContainerRef;

	/**
	 * @description
	 * The product currently being displayed
	 */
	@Input() product: ProcessedNonBundleProduct;

	scoreCardData: IScoreCardSkuResponse[];

	get criteoViewItemMetadata(): CriteoViewItemMetadata {
		return {
			item: this.product?.skus[0]?.partNumber,
			price: this.product?.skus[0]?.prices?.offerPrice?.toString(),
			availability: this.product?.skus[0]?.buyable,
			parentItem: this.product?.skus[0]?.parentPartNumber
		};
	}

	/**
	 * @description
	 * The kill switches
	 */
	@Input() killSwitches: KillSwitches;

	/**
	 * @description
	 * The promotions to be displayed
	 */
	@Input() promotions: PromotionDetailsMapV1 | PromotionDetailsMapV2;

	/**
	 * @description
	 * Product Launch Config
	 */
	@Input() launchConfig: LaunchConfig;

	/**
	 * @description
	 * Ecomm inventory map for product sku id list
	 */
	@Input() shipToMeInventoryMap: ISkuLocation[];

	/**
	 * @description
	 * Bopis store location inventory
	 */
	@Input() bopisLocationInventoryMap: ISkuLocation[];

	homrStatSliderLoaded: boolean;

	/**
	 * @description
	 * ATC Event Data
	 */
	atcEventData: ATCEvent;

	/**
	 * @description
	 * Flag to determine if Google PLA functionality is enabled
	 */
	isPLAEnabled = false;

	/**
	 * @description
	 * Flag to determine if the Certona resonance response is ready
	 */
	isResonanceReady: boolean;

	/**
	 * @description
	 * The currently resolved SKU
	 */
	resolvedSku: ProductSku;

	/**
	 * @description
	 * Subscription for Certona resonance response
	 */
	resonanceSubscriptions: Subscription = new Subscription();

	/**
	 * @description
	 * Certona scheme name
	 */
	scheme1 = 'product_rr';

	/**
	 * @description
	 * Certona scheme name
	 */
	scheme2 = 'product1_rr';

	/**
	 * @description
	 * Certona scheme name
	 */
	scheme3 = 'product2_rr';

	/**
	 * @description
	 * Certona scheme name
	 */
	oosScheme = 'skuoos_rr';

	/**
	 * @description
	 * The currently selected color attribute value if applicable
	 */
	selectedColorAttribute: string;

	/**
	 * @description
	 * The original value of the currently selected color attribute
	 */
	unformattedSelectedColorAttribute: string;

	/**
	 * @description
	 * If it's mobile viewport
	 * NOTE: Please don't initialize this flag. The undefined value is used for loading
	 * mobile components in SSR and then hidden them in the browser using media queries
	 * when the viewport is for desktop
	 */
	isMobile: boolean;
	isDesktopExtraLarge: boolean;

	/**
	 * @description
	 * Boolean used to determine if current state should show recs for an out of stock product.
	 */
	shouldShowOutOfStockRecs = false;

	/**
	 * @description
	 * Determine if we should show in store services content
	 */
	InStoreServicesEnabled: boolean;

	signedInStatus: boolean;

	syndigoATCAnalytics: boolean;

	/**
	 * @description
	 * Determines whether to show alert box for low stock.
	 */
	shouldShowAlert: boolean;

	backgroundImage: string;

	primaryCategoryBreadcrumb: string;

	isNikeProduct: boolean;

	sizeandFitSummary: string[] = [];

	/** @description has return policy attribute to show return-policy-v2 component */
	hasReturnPolicy = false;

	/** @description brand/manufacturer for product */
	brand = '';

	/** @description should show exclusive product ad */
	shouldShowExclusiveContentAd = false;

	nikeLoginSuccess = false;

	exclusiveType = '';

	exclusiveCTAType = '';

	/************ START OF FEATURE FLAGS *********************/

	searchRecommendationsKillsSwitch: boolean;

	frequentlyBoughtTogetherKillSwitchEnabled: boolean;

	showSponsoredProducts: boolean;

	/** @description killswitch for sustainability icons */
	sustainablityIconsEnabled: boolean;

	/**  @description Determines whether to show the info summary on product description */
	infoSummaryEnabled: boolean;

	hideBackToSearchButton = false;

	/** @description Add Pre Order content; */
	preOrderEnabled: boolean;

	/** @description Determine if we should show affirm pay later messaging */
	affirmPayLaterEnabled: boolean;

	/** @description Determine if we should show paypal pay later messaging */
	paypalPayLaterEnabled: boolean;

	/** @description feature flag for breadcrumbs */
	browseBreadcrumbsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('browseBreadcrumbsEnabled')
	);

	/** @description Determine if stylitics is enabled for environment */
	readonly styliticsEnabled = this.ngRxstore.selectSignal(selectKillswitch('styliticsEnabled'));

	/** @description Enable/Disable Stylitics Model Outfit Badge */
	readonly styliticsShopTheLookEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('styliticsShopTheLookEnabled')
	);

	/** @description Determine if stylitics mix and match is enabled for environment */
	readonly styliticsMnMEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('styliticsMnMEnabled')
	);
	readonly styliticsShopTheSetEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('styliticsShopTheSetEnabled')
	);

	/** @description Determine if Syndigo is enabled for environment */
	syndigoEnabled = this.ngRxstore.selectSignal(selectKillswitch('syndigoEnabled'));

	/** @description Feature flag for AEM content. */
	enableAEMContent: boolean;

	/**
	 * @description used for SDD fulfillment button
	 */
	sameDayDeliveryEnabled: boolean;

	/**
	 * @description determines whether or not to display EDD message on shipping button
	 */
	skuEDDEnabled: boolean;

	/**
	 * @description Feature flag for whether to show the content zone on non-exclusive pdps
	 */
	nonExclusiveContentZoneEnabled: boolean;

	/**
	 * @description used for nike exclusive products
	 */
	isNikeConnectedEnabled: boolean;

	/**
	 * @description used for gold exclusive products
	 */
	isGoldExclusiveEnabled = false;

	/**
	 * @description
	 * Boolean representing if current user has logged in and unlocked the exclusive product type.
	 */
	isAuthenticatedAndExclusiveUnlocked: boolean;

	/**
	 * @description
	 * Feature flag for new homefield accordian
	 */
	isProductDescriptionAccordionDisabled = true;

	/**
	 * @description feature flag for Sketchfab 3D imagery component
	 */
	isSketchfab3dEnabled = false;

	skuIdQueryParam: string;

	/**
	 * @description
	 * Feature flag for PDP ProTips.
	 */
	isPDPProTipsEnabled = this.ngRxstore.selectSignal(selectKillswitch('isPDPProTipsEnabled'));

	enhancedContentEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('enhancedContentEnabled')
	);

	isComparisonTableEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isComparisonTableEnabled')
	);

	sizeSelectionPanelEnabled = false;

	/**
	 * @description
	 * Feature flag for survey tooltip on bottom/grid ad
	 */
	enableTooltipSurvey = false;

	/********* END OF FEATURE FLAGS ******************/

	/**
	 * @description
	 * Determines whether or not to show the in store only fulfillment scenario
	 * based of buyable attribute from the API
	 */
	inStoreOnlyFulfillmentScenario$ = this.ngRxstore.select(selectIsInStoreOnlyProduct);

	resolvedSkuPartNumber$ = this.ngRxstore.select(selectResolvedSkuPartNumber);

	resolvedSku$ = this.ngRxstore.select(selectResolvedSku);

	userSelectedStoreName$ = this.ngRxstore.select(selectUserStoreName);

	isComingSoon$ = this.ngRxstore.select(selectIsComingSoon);
	selectShouldShowOutOfStockRecs$ = this.ngRxstore.select(selectShouldShowOutOfStockRecs);
	nearbyStoreCount$ = this.ngRxstore.select(selectNearbyAvailableStoreCount);
	selectTextileLabelInfo$ = this.ngRxstore.select(selectTextileLabelInfo);
	selectShouldShowAlternativeInStockRecs$ = this.ngRxstore.select(
		selectShouldShowAlternativeInStockRecs
	);

	isBOPISLOnly = this.ngRxstore.selectSignal(selectIsBOPISLOnly);

	isProductWarrantyEligible = this.ngRxstore.selectSignal(selectIsProductWarrantyEligible);

	brandLink = this.ngRxstore.selectSignal(selectBrandLink);

	styliticsWidgetShown = this.ngRxstore.selectSignal(selectStyliticsWidgetShown);
	productHasColorWithStylitics = this.ngRxstore.selectSignal(selectProductHasColorWithStylitics);

	readonly newDataScienceRecommendationsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('newDataScienceRecommendationsEnabled')
	);
	isPLADSCarouselEnabled = false;
	pdpGoogleAdManagerEnabled: boolean;

	alternativeInStockRecsEnabled: boolean;
	isApparelOrFootwear = false;

	/**
	 * @description
	 * Feature flag review widget.
	 */
	reviewsOff = false;
	/**
	 * @description
	 * Feature flag new reviews widget.
	 */
	newReviews = false;
	reviewsMVPRetest = false;
	exclusiveEnabled = false;
	readonly dataScienceRecommendationsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('dataScienceRecommendationsEnabled')
	);
	dataScienceRecommendationsLoaded: boolean;
	plaDataScienceRecommendationsErrored: boolean;
	pickupInOneHourTextEnabled = false;
	destroy$ = new Subject<void>();
	colorSelectionPanelEnabled: boolean;
	readonly newDataScienceRecommendationsEnabledNoPer = this.ngRxstore.selectSignal(
		selectKillswitch('newDataScienceRecommendationsEnabledNoPer')
	);
	dropdownAttributesEnabled = false;
	useAboveTheFoldMobileContainer = this.ngRxstore.selectSignal(
		selectKillswitch('useAboveTheFoldMobileContainer')
	);
	wasAboveTheFoldContentLoaded = false;
	isLoadingStateRecsEnabled: boolean;
	isUtahLawLabelAccordionEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isUtahLawLabelAccordionEnabled')
	);
	isSSRPLAEnabled = false;
	isReviewImgPlacementEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isReviewImgPlacementEnabled')
	);
	rightHandRailYouMayAlsoLikeEnabled = false;
	rightHandRailCustomersAlsoBoughtEnabled = false;
	isShoppableBVImageEnabled = false;
	warrantiesEnabled = this.ngRxstore.selectSignal(selectKillswitch('warrantiesEnabled'));
	sketchfab3DData;
	homrWireSpot = false;
	lazyHomrGTGTContent: HomrGTGTContent;
	lazyHomrBannerContent: IHomrBanner;
	lazyHomrInStoreServicesContent: IHomrRichContent;
	/**
	 * @description
	 * Feature flags for Social Images features
	 */
	isSocialImagesEnabled = this.ngRxstore.selectSignal(selectKillswitch('isSocialImagesEnabled'));
	isSocialImageAboveReviewsEnabled = false;
	isSocialImageBelowProductInfoEnabled = false;

	/**
	 * @description
	 * Feature flag to show the top 3 highly rated features
	 */
	isHighlyRatedReviewsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);

	readyForNextRoundAddOnsEnabled = false;

	isBrandLinkEnabled = this.ngRxstore.selectSignal(selectKillswitch('isBrandLinkEnabled'));
	isJumpLinkEnabled = this.ngRxstore.selectSignal(selectKillswitch('isJumpLinkEnabled'));

	/** @description Determine if we should show Score Reward Message */
	isScorerewardsMessageEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isScorerewardsMessageEnabled')
	);

	/* Event so that adobe target can set this flag after page load */
	@HostListener('window:readyForNextRoundAddOnsEnabled')
	public onReadyForNextRoundAddOnsEnabled() {
		this.readyForNextRoundAddOnsEnabled = true;
	}

	/* Event from adobe target for BV Shoppable Static Images*/
	@HostListener('window:isShoppableBVImageEnabled')
	public onShopableBVImageEnabled() {
		this.isShoppableBVImageEnabled = true;
	}

	/**
	 * PLA check we use when isSSRPLAEnabled is enabled.
	 */
	serverSafePLACheck = false;

	/**
	 * @description
	 * replaces the isPlatformBrowser(this.platformId) function call
	 */
	isPlatformBrowser = false;

	/**
	 * @description
	 * flag to toggle BOPUIS text
	 */
	nearbyStoresButtonEnabled = false;

	openNotifyMePanel = false;

	homrSliderData: HomrProductStatsSlider[];

	homrRichContent: IHomrRichContent;
	homrTextLinkContent: IHomrRichContent;

	isPLAPlaceholderEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isPLAPlaceholderEnabled')
	);
	plaPlaceholderClass = PLA_PLACEHOLDER_HIDDEN_CLASS;

	readonly templateName = DynamicProductTemplateModules.IN_STOCK_DEFAULT as const;

	constructor() {
		if (isPlatformBrowser(this.platformId)) {
			effect(() => {
				const breakpoint = this.breakpointsService.breakpoint();
				this.isMobile = breakpoint <= BreakPointNew.SMALL;
				this.isDesktopExtraLarge = breakpoint >= BreakPointNew.EXTRALARGE;
			});
		}
	}

	// eslint-disable-next-line max-lines-per-function
	ngOnInit(): void {
		this.isPlatformBrowser = isPlatformBrowser(this.platformId);
		this.searchRecommendationsKillsSwitch = this.utilityService.checkKillSwitch(
			'oosRecommendations',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.pickupInOneHourTextEnabled = this.utilityService.checkKillSwitch(
			'pickupInOneHourTextEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.rightHandRailYouMayAlsoLikeEnabled = this.utilityService.checkKillSwitch(
			'rightHandRailYouMayAlsoLikeEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.rightHandRailCustomersAlsoBoughtEnabled = this.utilityService.checkKillSwitch(
			'rightHandRailCustomersAlsoBoughtEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		const queryString = this.httpService.getQueryString();

		if (QueryStringParamService.doesQueryStringHaveParameter(queryString, 'sku')) {
			// set the sku id passed from the param
			this.skuIdQueryParam = this.httpService.getParameterValuesFromQueryString(
				queryString,
				'sku'
			);
		}
		this.sustainablityIconsEnabled = this.utilityService.checkKillSwitch(
			'sustainablityIconsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.infoSummaryEnabled = this.utilityService.checkKillSwitch(
			'infoSummaryEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.pdpGoogleAdManagerEnabled = this.utilityService.checkKillSwitch(
			'pdpGoogleAdManagerEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.enableTooltipSurvey = this.utilityService.checkKillSwitch(
			'enableTooltipSurvey',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.resonanceSubscriptions.add(
			this.certonaService.resonanceReadyState.subscribe((resonanceReady) => {
				this.isResonanceReady = resonanceReady?.isPLAEnabled;
			})
		);

		this.preOrderEnabled = this.utilityService.checkKillSwitch(
			'preOrderEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.sameDayDeliveryEnabled = this.utilityService.checkKillSwitch(
			'sameDayDeliveryEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.skuEDDEnabled = this.utilityService.checkKillSwitch(
			'skuEDDEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.nonExclusiveContentZoneEnabled = this.utilityService.checkKillSwitch(
			'nonExclusiveContentZoneEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isNikeConnectedEnabled = this.utilityService.checkKillSwitch(
			'isNikeConnectedEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.isPLADSCarouselEnabled = this.utilityService.checkKillSwitch(
			'isPLADSCarouselEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.alternativeInStockRecsEnabled = this.utilityService.checkKillSwitch(
			'alternativeInStockRecsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);
		this.isProductDescriptionAccordionDisabled = this.utilityService.checkKillSwitch(
			'isProductDescriptionAccordionDisabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.colorSelectionPanelEnabled = this.utilityService.checkKillSwitch(
			'colorSelectionPanelEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.newReviews = this.utilityService.checkKillSwitch(
			'newReviews',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.reviewsMVPRetest = this.utilityService.checkKillSwitch(
			'reviewsMVPRetest',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.sizeSelectionPanelEnabled = this.utilityService.checkKillSwitch(
			'sizeSelectionPanelEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isSketchfab3dEnabled = this.utilityService.checkKillSwitch(
			'isSketchfab3dEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.dropdownAttributesEnabled = this.utilityService.checkKillSwitch(
			'dropdownAttributesEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.nearbyStoresButtonEnabled = this.utilityService.checkKillSwitch(
			'nearbyStoresButtonEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isLoadingStateRecsEnabled = this.utilityService.checkKillSwitch(
			'isLoadingStateRecsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isPLAEnabled = this.utilityService.checkKillSwitch(
			'isPLAEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isSSRPLAEnabled = this.utilityService.checkKillSwitch(
			'isSSRPLAEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.readyForNextRoundAddOnsEnabled = this.utilityService.checkKillSwitch(
			'readyForNextRoundAddOnsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isSocialImageAboveReviewsEnabled = this.utilityService.checkKillSwitch(
			'isSocialImageAboveReviewsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.isSocialImageBelowProductInfoEnabled = this.utilityService.checkKillSwitch(
			'isSocialImageBelowProductInfoEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		this.ngRxstore.dispatch(
			setNearbyStoresButtonEnabledFlag({
				nearbyStoresButtonEnabled: this.nearbyStoresButtonEnabled
			})
		);

		if (this.alternativeInStockRecsEnabled) {
			this.checkForApparelAndFootwearAttributeNames();
		}

		this.exclusiveType = this.getExclusiveNameIfAvailable();

		// Check if product is buyable
		if (this.product) {
			this.product.buyable = this.utilityService.isTruthy(this.product.buyable);

			// Primary Category for Breaddcrumbs
			if (this.product.primaryCategory) {
				this.primaryCategoryBreadcrumb = this.product.primaryCategory;
			}
		}

		// Set the Required Descriptive Attributes
		if (this.product?.descriptiveAttributes) {
			const attributes = this.product.descriptiveAttributes;

			// Find and check if the product is a Nike APE exclusive
			const nikeApeExclusive = attributes.find(
				(attribute) =>
					attribute &&
					attribute.identifier === '5632' &&
					`${attribute.value}`.toLowerCase() === 'nike ape exclusive'
			);
			this.isNikeProduct = !!nikeApeExclusive;

			// Find and set the brand, and determine if exclusive content ad should be shown
			const brandAttribute = attributes.find(
				(attribute) => attribute && attribute.identifier === 'X_BRAND'
			);

			if (brandAttribute) {
				this.brand = `${brandAttribute.value}`;
				this.shouldShowExclusiveContentAd =
					exclusiveBrands.includes(this.brand) &&
					!this.isNikeProduct &&
					this.nonExclusiveContentZoneEnabled;
			}

			// Find and process the Size and Fit Summary attribute
			const sizeAndFitAttribute = attributes.find(
				(attribute) =>
					attribute &&
					attribute.identifier === '5426' &&
					attribute.name.toLowerCase() === 'size and fit summary'
			);

			if (sizeAndFitAttribute) {
				const tempArray = `${sizeAndFitAttribute.value}`.split('|');
				tempArray.forEach((value) => {
					this.sizeandFitSummary.push(value);
				});
			}

			// Find and flag the presence of a return policy
			const returnPolicyAttribute = attributes.find(
				(attribute) => attribute && attribute.identifier === '5749'
			);
			this.hasReturnPolicy = !!returnPolicyAttribute;
		}

		// Ensure there is something that was set
		const defaultColor = this.productService.determineProductDefaultColor(this.product);

		if (defaultColor !== undefined) {
			// Default the selected color attribute
			this.unformattedSelectedColorAttribute = defaultColor;
			this.selectedColorAttribute = defaultColor.replace(/[/ -]/g, '_');
		}

		/*
		 * PLA
		 */
		this.serverSafePLACheck =
			this.isPLAEnabled &&
			this.isSSRPLAEnabled &&
			this.productService.isPLA(this.isPlatformBrowser);

		this.setupPlaPlatformBrowserContent();

		this.ngRxstore
			.select(selectSelectedColorAttribute)
			.pipe(takeUntil(this.destroy$))
			.subscribe((attributeSelected: AttributeOption) => {
				if (attributeSelected) {
					this.unformattedSelectedColorAttribute = attributeSelected.value;
					this.selectedColorAttribute = attributeSelected.value.replace(/[/ -]/g, '_');
				}
			});

		if (this.shouldShowPLA()) {
			this.rightHandRailCustomersAlsoBoughtEnabled = false;
			this.rightHandRailYouMayAlsoLikeEnabled = false;
		}
	}

	private setupPlaPlatformBrowserContent(): void {
		// Check if current context is browser
		if (this.isPlatformBrowser) {
			if (this.killSwitches) {
				// KILL SWITCHES
				this.frequentlyBoughtTogetherKillSwitchEnabled =
					this.utilityService.checkKillSwitch(
						'frequentlyBoughtTogetherEnabled',
						this.killSwitches,
						this.chain.chainIdentifierAbbr
					);
				this.affirmPayLaterEnabled = this.utilityService.checkKillSwitch(
					'affirmPayLaterEnabled',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);
				this.paypalPayLaterEnabled = this.utilityService.checkKillSwitch(
					'paypalPayLaterEnabled',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);
				this.showSponsoredProducts = this.utilityService.checkKillSwitch(
					'showSponsoredProducts',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);

				this.enableAEMContent = this.utilityService.checkKillSwitch(
					'enableAEMContent',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);
				this.isGoldExclusiveEnabled = this.utilityService.checkKillSwitch(
					'isGoldExclusiveEnabled',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);
				this.reviewsOff = this.utilityService.checkKillSwitch(
					'reviewsOff',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);

				this.isShoppableBVImageEnabled = this.utilityService.checkKillSwitch(
					'isShoppableBVImageEnabled',
					this.killSwitches,
					this.chain.chainIdentifierAbbr
				);
			}

			this.updatePlaAndBackToSearchState();

			if (typeof this.route?.queryParams !== 'undefined') {
				this.utilityService.isTruthy(
					this.route.queryParams.subscribe((value) => {
						this.nikeLoginSuccess = value.CONNECTION_STATUS === 'SUCCESS';
						if (this.nikeLoginSuccess) {
							// Removing CONNECTION_STATUS query param from history to avoid overlay reshowing when users click back
							history.replaceState(null, null, this.window.location.pathname);
							// Clear old party attributes, so we can retrieve the latest
							sessionStorage.removeItem('partyAttributes');
						}
					})
				);
			}

			this.store
				.selectDistinctKey('HOMR_DATA')
				.pipe(
					takeUntil(this.destroy$),
					filter((data) => data !== undefined)
				)
				.subscribe((data: IHomrState) => {
					if ('homrRichContent' in data) {
						if (data.homrRichContent.productStatsSlider) {
							this.homrSliderData = data.homrRichContent.productStatsSlider;
							this.homrStatSliderLoaded = true;
						} else if (this.shouldHomrRichContentBeDisplayed(data.homrRichContent)) {
							this.homrRichContent = data.homrRichContent;
						}
						const sketchfab3DContent = this.utilityService.findPropertyInObject(
							data.homrRichContent,
							'pdp3DImagery'
						);
						if (sketchfab3DContent && this.isSketchfab3dEnabled) {
							this.sketchfab3DData = sketchfab3DContent;
						}
					}
					if ('homrContent' in data) {
						this.homrTextLinkContent = data.homrContent;
					}
					if ('homrBanner' in data) {
						this.lazyHomrBannerContent = data.homrBanner;
					}
					if (
						'homrInStoreServices' in data &&
						this.utilityService.checkKillSwitch(
							'inStoreServicesEnabled',
							this.killSwitches,
							this.chain.chainIdentifierAbbr
						)
					) {
						this.lazyHomrInStoreServicesContent = data.homrInStoreServices;
					}
					if (
						this.enhancedContentEnabled() &&
						data.homrRichContent?.enhancedProductCard?.length
					) {
						this.homrWireSpot = true;
					}
				});

			this.ngRxstore
				.select(selectHomrGTGTContent)
				.pipe(take(2))
				.subscribe((homrData) => {
					if (homrData) {
						this.lazyHomrGTGTContent = homrData;
					}
				});

			// scorecard data
			this.store
				.selectDistinctKey('SCORECARD_DATA')
				.pipe(
					takeUntil(this.destroy$),
					filter((data) => data !== undefined)
				)
				.subscribe((data: IScoreCardSkuResponse[]) => {
					this.scoreCardData = data;
				});

			this.exclusiveEnabled = this.showExclusive();
		}

		if (this.chain.chainIdentifierAbbr === 'pl') {
			this.backgroundImage = `url('assets/image/Background_PL_PDP.png')`;
		}
	}

	private updatePlaAndBackToSearchState(): void {
		if (this.isPLAEnabled) {
			const isPLA = this.isSSRPLAEnabled
				? this.serverSafePLACheck
				: this.productService.setupPla(this.document.referrer, this.route);

			// If PLA is not triggered, turn off PLA
			this.isPLAEnabled = this.isPLAEnabled && isPLA;

			this.hideBackToSearchButton = isPLA;

			if (this.isPLAEnabled) {
				this.analyticsService.setPageTemplate('PLA');
			}
		}
	}

	ngAfterViewInit(): void {
		// When the app is loaded in the browser we want to move the Above the Fold content that is initially rendered
		// in the product container by detaching it and then inserting it into this component's template without destroying the instance.
		if (
			isPlatformBrowser(this.platformId) &&
			this.useAboveTheFoldMobileContainer() &&
			this.sourceAboveTheFoldViewRef
		) {
			const view = this.sourceAboveTheFoldViewRef.detach();
			// We use this flag to determine if the Above the Fold content was completely moved and remove
			// the temporary styles used for adding a placeholder of the end position of the Above the Fold content
			this.wasAboveTheFoldContentLoaded = true;
			this.targetAboveTheFoldViewRef.insert(view);
			// Fire change detection for wasAboveTheFoldContentLoaded flag
			this.changeDetectorRef.detectChanges();
		}

		if (this.exclusiveType && this.isPlatformBrowser) {
			if (this.window.party) {
				this.setAuthenticatedAndExclusiveConnectedStatus(
					partyAttributes[this.exclusiveType?.toLowerCase()],
					this.exclusiveType
				);
			} else {
				window.document.addEventListener('readyToParty', () => {
					this.setAuthenticatedAndExclusiveConnectedStatus(
						partyAttributes[this.exclusiveType?.toLowerCase()],
						this.exclusiveType
					);
				});
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Gets value from Syndigo component to determine if ATC should send tracking */
	syndigoATCFlag(data) {
		this.syndigoATCAnalytics = data;
	}

	signedInHandler(value: boolean) {
		this.signedInStatus = value;
	}

	/**
	 * @description Used to check if Nike connected button should be displayed.
	 */
	shouldNikeConnectedButtonBeDisplayed() {
		return (
			this.isNikeConnectedEnabled &&
			this.exclusiveType === 'Nike' &&
			this.isAuthenticatedAndExclusiveUnlocked === false
		);
	}

	shouldGoldExclusiveButtonBeDisplayed() {
		return (
			this.isGoldExclusiveEnabled &&
			this.exclusiveType === 'Gold' &&
			this.isAuthenticatedAndExclusiveUnlocked === false
		);
	}

	shouldHomrRichContentBeDisplayed(obj: IHomrRichContent) {
		let hasNonEmptyProperty = false;
		const propsToIgnore = ['pdp3DImagery', 'enhancedProductCard'];
		for (const prop in obj) {
			if (!propsToIgnore.includes(prop)) {
				if (typeof obj[prop] === 'object') {
					// Recursively check nested object
					if (this.shouldHomrRichContentBeDisplayed(obj[prop])) {
						hasNonEmptyProperty = true;
						break;
					}
				} else if (obj[prop] || (Array.isArray(obj[prop]) && obj[prop].length > 0)) {
					hasNonEmptyProperty = true;
					break;
				}
			}
		}
		return hasNonEmptyProperty;
	}

	/**
	 * @description Compares the Defining Attribute Name from the product and seeing if they are apparel or footwear related ones.
	 * @param productDefiningAttributeNames
	 * @return true if the product has footwear or apparel related Attr. Names which are listed in the footwearApparelAttrNames attr. array
	 */

	checkForApparelAndFootwearAttributeNames() {
		if (this.product?.definingAttributes && this.product?.definingAttributes.length > 0) {
			let allMatch = true;
			this.product.definingAttributes.forEach((attrName) => {
				const isMatch = footwearApparelAttrNames.find(
					(footwearApparelAttr) =>
						attrName.name.replace(/\s/g, '').toLowerCase() === footwearApparelAttr
				);
				if (!isMatch) {
					allMatch = false;
				}
			});
			this.isApparelOrFootwear = allMatch;
		}
	}

	/**
	 * TODO: This can be refactored a little better to remove the 'break' statements. One option is changing exclusiveApeValue to an
	 * array and checking for an indexOf or something.
	 * @description If product is exclusive, it will return the exclusive name. If not it will return an empty string.
	 */
	getExclusiveNameIfAvailable(): string {
		let exclusiveName = '';
		if (this.product?.skus) {
			for (const sku of this.product.skus) {
				const apeAttribute = sku.descriptiveAttributes.find(
					(attr) => attr.identifier === '5632'
				);
				for (const exclusiveType in exclusiveApeValue) {
					if (apeAttribute && apeAttribute.value === exclusiveApeValue[exclusiveType]) {
						exclusiveName = exclusiveType;
						break; // No need to  loop through rest of APE values after a match. Break out of loop.
					}
				}
				if (exclusiveName) {
					break;
				} // No need to loop through rest of skus after match. Break out of loop.
			}

			// capitalizing first letter to match exclusive name of json file
			exclusiveName = exclusiveName?.charAt(0).toUpperCase() + exclusiveName?.slice(1);
		}
		return exclusiveName;
	}

	/**
	 * @description Sets Exclusive CTA type based on exclusive type and if user is signed in.
	 */
	private setExclusiveCTAType(isUserLoggedIn: boolean, exclusiveName: string) {
		if (isUserLoggedIn) {
			this.exclusiveCTAType = exclusiveCTATypes[exclusiveName?.toLowerCase()]?.loggedIn;
		} else {
			this.exclusiveCTAType = exclusiveCTATypes[exclusiveName?.toLowerCase()]?.notLoggedIn;
		}
	}

	/**
	 * @description Gets Identity ID from party attribues in session storage, if not available returns an empty string.
	 */
	private getIdentityIdFromStoredPartyAttr() {
		const attr = JSON.parse(sessionStorage.getItem(`partyAttributes`));
		if (attr) {
			return JSON.parse(attr?.partyAttributes)?.identityId;
		}
		return '';
	}

	private setAuthenticatedAndExclusiveConnectedStatus(
		exclusivePartyAttrName: string,
		exclusiveName: string
	) {
		this.authService
			.getIsAuthenticated$()
			.pipe(
				takeUntil(this.destroy$),
				catchError((error) => {
					this.logger.error(
						'Error getting authentication for exclusive product. ',
						error
					);
					this.setExclusiveCTAType(false, exclusiveName);
					return of(false);
				}),
				map(async (loggedIn) => {
					this.setExclusiveCTAType(loggedIn, exclusiveName);
					if (loggedIn) {
						// if user logged in, retrieve identity ID
						await this.authService
							.getIdentityId$()
							.then((identityId) => {
								// retrieve identity ID from session storage, and if they don't match, clear party attributes from session
								if (identityId !== this.getIdentityIdFromStoredPartyAttr()) {
									sessionStorage.removeItem(partyAttrText);
								}
							})
							.catch(() => {
								sessionStorage.removeItem(partyAttrText);
							})
							.finally(() => {
								this.exclusiveContentService
									.isUserConnectedToSpecificParty(exclusivePartyAttrName)
									.pipe(
										map((isConnected) => {
											this.isAuthenticatedAndExclusiveUnlocked = isConnected;
										})
									)
									.subscribe();
							});
					} else {
						// not logged in, remove party attributes from session
						sessionStorage.removeItem(partyAttrText);
						this.isAuthenticatedAndExclusiveUnlocked = false;
					}
				})
			)
			.subscribe();
	}

	/**
	 * @description Used as a feature flag for different exclusive brands, currently used to check if gold exclusive feature flag is turned on.
	 */
	showExclusive(): boolean {
		return (
			this.exclusiveType &&
			(this.exclusiveType !== 'Gold' ||
				(this.exclusiveType === 'Gold' && this.isGoldExclusiveEnabled))
		); // feature flag for gold exclusive, can remove once live in prod
	}

	shouldShowPLA() {
		if (this.isSSRPLAEnabled) {
			if (!this.isPlatformBrowser && this.isPLADSCarouselEnabled) {
				return false; // homefield carousel does not support SSR yet
			}
			return this.serverSafePLACheck && !this.plaDataScienceRecommendationsErrored;
		}
		return (
			this.isPLAEnabled &&
			!this.plaDataScienceRecommendationsErrored &&
			this.isPlatformBrowser
		);
	}

	/**
	 * @description encapsulates spinner logic to reduce clutter in the spinner's ngIf. If there are any dataScienceRecommendation flags enabled, we should use dataScienceRecommendationsLoaded to show/hide the spinner. Otherwise we should use isResonanceReady.
	 */
	get shouldShowSpinner(): boolean {
		if (this.isLoadingStateRecsEnabled) {
			return false;
		}

		const anyDataScienceRecommendationEnabled =
			this.dataScienceRecommendationsEnabled() ||
			this.newDataScienceRecommendationsEnabled() ||
			this.newDataScienceRecommendationsEnabledNoPer();
		const allDataScienceRecommendationsDisabled =
			!this.dataScienceRecommendationsEnabled() &&
			!this.newDataScienceRecommendationsEnabled() &&
			!this.newDataScienceRecommendationsEnabledNoPer();

		if (anyDataScienceRecommendationEnabled) {
			return !this.dataScienceRecommendationsLoaded;
		} else {
			return !this.isResonanceReady && allDataScienceRecommendationsDisabled;
		}
	}

	openHighlyRatedFeatures() {
		this.highlyRatedFeaturesModal()?.openHighlyRatedFeatures();
	}
}
